import { FC, ReactNode, useState } from "react";
import { observer } from "mobx-react";
import { isFailure, Result } from "../common/common";
import { TMessage } from "../i18n/tt";
import { FullScreenLayout } from "../components-v2/FullScreenLayout";
import { useAppBar } from "../appBar/useAppBar";
import { TabButtonProps, Tabs } from "../components-v2/Tabs";
import { BottomSpacer, Box, Card, VBox } from "../components-v2/Box";
import { UserMenu } from "../components-v2/AppBar";
import { isMobileView } from "../utils/screen";
import { AppBarData, CompanyOption } from "../appBar/appBarDomain";
import {
  Attachments,
  DownloadAsPdfButtonProps,
  HeaderMeetingInfo,
  LogoWithCompanyName,
  MatterText,
  MeetingDocumentView,
  NoticeActionsView,
  AttachmentsCard,
} from "../common/commonComponents";
import { compact } from "lodash";
import { Button, LinkButton, ToggleButton } from "../components-v2/Button";
import { openDashboardForAccessType, PreviewState } from "../meetings/meetingsDomain";
import {
  GetMeetingEmailDocumentResponse,
  HeaderData,
  MatterForReview,
  normalizeMatterText,
} from "./noticeForBoardMemberDomain";
import { UploadedAttachment } from "../fileUpload/fileUploadDomain";
import { getDecisionActionLabel, isInformationMatterInDocument } from "../dbot/common/commonDomain";
import { Tag } from "../components-v2/Tag";
import { matterViewPage, portfolioUrl } from "../utils/navigation";
import { Alert } from "../components-v2/Alert";
import { Icon } from "../components-v2/Icon";
import { EmptyState } from "../components-v2/EmptyState";

import fileImage from "../assets/img/file-image.svg";
import { HiddenForMobile, VisibleOnMobile } from "../components-v2/Mobile";
import { Vote } from "../dbot/boardMeetingMinutes/boardMeetingVoting";
import AlertMessageStore from "../stores/AlertMessageStore";
import { FilePreviewDialog } from "../components-v2/FilePreview";
import { DocumentType } from "../common/documents";
import { useMessageStore, useRootStore, useTranslation } from "../stores/RootStoreContext";
import { CommentActions, CommentDTO, EditCommentState } from "../comments/commentsDomain";
import { CommentsView } from "../comments/commentsComponents";

export type BoardMeetingTabId = "notice" | "minutes" | "review" | "draft-minutes";

export type MeetingEmailActions = {
  onVoteForMatter?: (matter: MatterForReview, vote: Vote) => void;
  onDownloadAttachment?: (fileId: string) => void;
  onBack?: () => void;
  results: (Result<unknown, TMessage> | undefined)[];
  commentActions?: CommentActions;
};

export const NoticeOfBoardMemberView: FC<{
  data: GetMeetingEmailDocumentResponse;
  isReviewMinutes?: boolean;
  onDownloadAttachment: (fileId: string) => void;
  backUrl?: string;
  edit?: () => void;
  actions?: MeetingEmailActions;
  editComment?: EditCommentState;
  previewAttachmentState?: PreviewState;
  onCloseAttachmentPreview?: () => void;
  downloadNotice: DownloadAsPdfButtonProps;
}> = observer(
  ({
    data,
    isReviewMinutes,
    onDownloadAttachment,
    backUrl,
    edit,
    actions,
    editComment,
    previewAttachmentState,
    onCloseAttachmentPreview,
    downloadNotice,
  }) => {
    const alertStore = useMessageStore();
    const [active, setActive] = useState<BoardMeetingTabId>(isReviewMinutes ? "review" : "notice");
    const history = useRootStore().history;
    return (
      <FullScreenLayout
        appBar={
          <BoardMeetingHeader
            data={data}
            active={active}
            setActive={setActive}
            backUrl={backUrl}
            edit={edit}
            isReviewMinutes={isReviewMinutes}
          />
        }
        alertStore={alertStore}
        centerPanel={
          <>
            {isReviewMinutes ? (
              active === "draft-minutes" ? (
                <DraftMinutes content={data.draftMinutesDocumentContent} />
              ) : (
                <ReviewMinutesBody
                  {...actions}
                  editComment={editComment}
                  isMultiLanguage={data.isMultiLanguage}
                  meetingEmailId={data.meetingEmailId}
                  mattersForReview={data.mattersForReview}
                  comments={data.comments}
                  uploadedFiles={data.uploadedFiles}
                  onOpenMatter={(matter: MatterForReview) =>
                    history.push(matterViewPage(data.meetingEmailId, matter.decision.decisionId))
                  }
                  onDownloadAttachment={onDownloadAttachment}
                  onCloseAttachmentPreview={onCloseAttachmentPreview}
                  previewAttachmentState={previewAttachmentState}
                />
              )
            ) : (
              <BoardMeetingBody
                documentContent={
                  active === "notice"
                    ? data.noticeDocumentContent
                    : active === "minutes"
                      ? data.minutesDocumentContent
                      : undefined
                }
                attachments={data.uploadedFiles}
                onDownloadAttachment={onDownloadAttachment}
                previewAttachmentState={previewAttachmentState}
                onCloseAttachmentPreview={onCloseAttachmentPreview}
                downloadNotice={downloadNotice}
              />
            )}
          </>
        }
      />
    );
  },
);

export const BoardMeetingHeader: FC<{
  data: HeaderData;
  isReviewMinutes?: boolean;
  active: BoardMeetingTabId;
  setActive: (id: BoardMeetingTabId) => void;
  backUrl?: string;
  edit?: () => void;
}> = observer(({ data, isReviewMinutes, active, setActive, backUrl, edit }) => {
  const selectedCompany: CompanyOption = {
    organisationNumber: data.orgNo ?? "",
    name: data.companyName ?? "",
    accessType: data.accessType,
  };
  let appBar = useAppBar({ withoutMenu: true, withoutUserSettings: true });
  if (appBar !== null) {
    appBar = { ...appBar, selectedCompany: selectedCompany };
  }
  const { t } = useTranslation();
  const tabs: TabButtonProps[] = compact([
    !isReviewMinutes && {
      id: "notice",
      isActive: active === "notice",
      label: t("meetings.notice"),
      onSelect: () => setActive("notice"),
    },
    data.minutesDocumentContent && {
      id: "minutes",
      isActive: active === "minutes",
      label: t("meetings.minutes"),
      onSelect: () => setActive("minutes"),
    },
    isReviewMinutes && {
      id: "review",
      isActive: active === "review",
      label: t("meetings.review"),
      onSelect: () => setActive("review"),
    },
    isReviewMinutes && {
      id: "draft-minutes",
      isActive: active === "draft-minutes",
      label: (
        <>
          {t("meetings.minutes")} <Tag label={t("draft")} color="neutral" />
        </>
      ),
      onSelect: () => setActive("draft-minutes"),
    },
  ]);
  return (
    <>
      <VisibleOnMobile>
        <VBox borderBottom bg gap={false}>
          <Box justifyContent="space-between" padding={"sm"}>
            <LogoWithCompanyName
              companyName={data.companyName}
              orgNo={data.orgNo}
              backUrl={backUrl}
            />
            <BoardMemberAppBar appBar={appBar} data={data} />
          </Box>
          <Box paddingX paddingTop>
            <HeaderMeetingInfo
              title={t("dashboard.boardMeeting")}
              companyName={data.companyName ?? ""}
              meetingStartDate={data.meetingStartDate ?? ""}
              meetingEndDate={data.meetingEndDate ?? ""}
              ical={data.ical}
              googleCalendar={data.googleCalendar}
              orgNo={data.orgNo}
              edit={edit}
              isCirculation={data.isCirculation}
              deadlineForFeedback={data.deadlineForFeedback}
            />
          </Box>
          <Tabs scrollable={false} tabs={tabs} />
        </VBox>
      </VisibleOnMobile>
      <HiddenForMobile>
        <VBox borderBottom bg gap={false}>
          <Box
            paddingLeft={"md"}
            paddingTop={"sm"}
            padding
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <LogoWithCompanyName
              companyName={data.companyName}
              orgNo={data.orgNo}
              backUrl={backUrl}
            />
            <BoardMemberAppBar appBar={appBar} data={data} />
          </Box>
          <VBox container paddingX={"lg"} maxWidth="xl">
            <HeaderMeetingInfo
              title={t("dashboard.boardMeeting")}
              companyName={data.companyName ?? ""}
              meetingStartDate={data.meetingStartDate ?? ""}
              meetingEndDate={data.meetingEndDate ?? ""}
              ical={data.ical}
              googleCalendar={data.googleCalendar}
              orgNo={data.orgNo}
              edit={edit}
              isCirculation={data.isCirculation}
              deadlineForFeedback={data.deadlineForFeedback}
            />
            <Tabs scrollable={false} tabs={tabs} />
          </VBox>
        </VBox>
      </HiddenForMobile>
    </>
  );
});

const BoardMemberAppBar: FC<{ appBar: AppBarData | null; data: HeaderData }> = observer(
  ({ appBar, data }) => {
    const { t } = useTranslation();
    const link = openDashboardForAccessType(data.accessType, data.orgNo ?? "");
    const history = useRootStore().history;
    return appBar ? (
      <UserMenu
        languages={appBar?.languages ?? []}
        user={{ name: data.recipientName ?? "", initials: data.recipientInitials ?? "" }}
        portfolioUrl={link ? link.url : portfolioUrl}
        companies={[]}
        actions={appBar?.actions ?? []}
        selectedCompany={data.companyroleId !== undefined ? appBar?.selectedCompany : undefined}
        userMainAction={
          link && data.companyroleId !== undefined ? (
            <Button
              label={t(link.label)}
              variant="secondary"
              size="sm"
              icon="arrow-square-out"
              onClick={() => history.push(link.url)}
            />
          ) : undefined
        }
      />
    ) : null;
  },
);

export const BoardMeetingBody: FC<{
  documentContent?: string;
  onDownloadAttachment: (fileId: string) => void;
  attachments: UploadedAttachment[];
  previewAttachmentState?: PreviewState;
  onCloseAttachmentPreview?: () => void;
  downloadNotice: DownloadAsPdfButtonProps;
}> = observer(
  ({
    documentContent,
    onDownloadAttachment,
    attachments,
    previewAttachmentState,
    onCloseAttachmentPreview,
    downloadNotice,
  }) => {
    return (
      <Box
        gap={isMobileView() ? "sm" : "lg"}
        fullheight
        padding={!isMobileView() ? "lg" : undefined}
        paddingTop={isMobileView()}
        maxWidth={"xl"}
        container
        vertical={isMobileView()}
      >
        {previewAttachmentState?.type === "preview" && (
          <FilePreviewDialog
            filename={previewAttachmentState.filename}
            file={previewAttachmentState.file}
            onClose={onCloseAttachmentPreview}
          />
        )}
        <MeetingDocumentView content={documentContent} />
        <VBox gap={"lg"} paddingTop={isMobileView()} className="tw-max-w-[340px]">
          <NoticeActionsView download={downloadNotice} />
          <AttachmentsCard attachments={attachments} onDownload={onDownloadAttachment} />
        </VBox>
        {isMobileView() && <BottomSpacer />}
      </Box>
    );
  },
);

const MatterMetadata: FC<{
  matter: MatterForReview;
}> = observer(({ matter }) => {
  const yes = matter.votes.filter((v) => v.vote === "for").length;
  const no = matter.votes.filter((v) => v.vote === "against").length;
  const attachments = matter.attachments.length;
  return (
    <Box alignItems="center" gap="md">
      {yes > 0 && (
        <Box alignItems="center" gap="xs">
          <Icon name={"thumbs-up"} />
          <span className="text-sm">{yes}</span>
        </Box>
      )}
      {no > 0 && (
        <Box alignItems="center" gap="xs">
          <Icon name={"thumbs-down"} />
          <span className="text-sm">{no}</span>
        </Box>
      )}
      <Box alignItems="center" gap="xs">
        <Icon name={"chat-text"} />
        <span className="text-sm">{matter.comments.length}</span>
      </Box>
      {attachments > 0 && (
        <Box alignItems="center" gap="xs">
          <Icon name={"paperclip"} /> <span className="text-sm">{attachments}</span>
        </Box>
      )}
    </Box>
  );
});

export const DraftMinutes: FC<{ content?: string }> = observer(({ content }) => {
  return (
    <Box
      gap={isMobileView() ? "sm" : "lg"}
      fullheight
      padding={!isMobileView() ? "lg" : undefined}
      paddingTop={isMobileView()}
      maxWidth={"xl"}
      container
      vertical={isMobileView()}
    >
      <MeetingDocumentView content={content} />
    </Box>
  );
});

export const ReviewMinutesBody: FC<{
  meetingEmailId?: string;
  mattersForReview: MatterForReview[];
  uploadedFiles: UploadedAttachment[];
  onOpenMatter: (matter: MatterForReview) => void;
  onDownloadAttachment?: (fileId: string) => void;
  results?: (Result<unknown, TMessage> | undefined)[];
  previewAttachmentState?: PreviewState;
  onCloseAttachmentPreview?: () => void;
  comments: CommentDTO[];
  isMultiLanguage: boolean;
  commentActions?: CommentActions;
  editComment?: EditCommentState;
}> = observer(
  ({
    mattersForReview,
    uploadedFiles,
    onOpenMatter,
    onDownloadAttachment,
    results,
    comments,
    previewAttachmentState,
    onCloseAttachmentPreview,
    commentActions,
    editComment,
  }) => {
    const { t } = useTranslation();
    return (
      <BodyContainer>
        {mattersForReview.length === 0 && (
          <EmptyState
            illustration={<img src={fileImage} alt={t("no-matters-to-review")} />}
            title={t("no-matters-to-review")}
            description={t("no-matters-to-review-description")}
          />
        )}
        {mattersForReview.map((matter) => {
          const text = normalizeMatterText(matter);
          return (
            <button onClick={() => onOpenMatter(matter)} key={matter.decision.decisionId}>
              <Card maxWidth="xl" hoverBg dropShadow={false}>
                <h4>
                  {t(
                    getDecisionActionLabel(
                      matter.decision.decisionData.type,
                      matter.decision.decisionData,
                    ),
                  )}
                </h4>
                {isInformationMatterInDocument(
                  matter.decision.decisionData,
                  DocumentType.board_meeting,
                ) ? (
                  <div className={"caption"}>{t("meetings.decision.information_matter")}</div>
                ) : (
                  <Tag label={t("meetings.decision.to_review")} color="info" />
                )}
                <div className={"subtle tw-line-clamp-2"}>{text}</div>
                <MatterMetadata matter={matter} />
              </Card>
            </button>
          );
        })}
        {previewAttachmentState?.type === "preview" && (
          <FilePreviewDialog
            filename={previewAttachmentState.filename}
            file={previewAttachmentState.file}
            onClose={onCloseAttachmentPreview}
          />
        )}
        <Attachments
          attachments={uploadedFiles}
          onDownloadAttachment={onDownloadAttachment}
          caption={{ key: "all-attachments" }}
        />
        {results?.some((result) => isFailure(result)) && (
          <Alert type={"error"} message={t("general-error")} />
        )}
        {commentActions && (
          <CommentsView comments={comments} editState={editComment} actions={commentActions} />
        )}
      </BodyContainer>
    );
  },
);

type MatterViewProps = {
  matter: MatterForReview;
  companyroleId?: number;
  onBack?: () => void;
  onDownloadAttachment?: (fileId: string) => void;
  onVoteForMatter?: (matter: MatterForReview, vote: Vote) => void;
  resetVote?: () => void;
  results?: (Result<unknown, TMessage> | undefined)[];
  messageStore: AlertMessageStore;
  fullscreen?: boolean;
  editComment?: EditCommentState;
  commentActions?: CommentActions;
};

export const MatterView: FC<MatterViewProps> = observer(
  ({
    matter,
    companyroleId,
    onVoteForMatter,
    resetVote,
    onBack,
    onDownloadAttachment,
    results,
    messageStore,
    fullscreen,
    commentActions,
    editComment,
  }) => {
    const { t } = useTranslation();
    const vote = matter?.votes.find((vote) => vote.companyroleId === companyroleId)?.vote;
    const body = (
      <BodyContainer>
        <VBox grow padding gap>
          {onBack && (
            <Box marginBottom>
              <LinkButton label={t("back")} icon="arrow-left" onClick={onBack} />
            </Box>
          )}
          <Title matter={matter} />
          {results?.some((result) => isFailure(result)) && (
            <Alert type={"error"} message={t("general-error")} />
          )}
          <MatterText
            matterText={matter.textForReview}
            isMultiLanguage={matter.isMultiLanguage}
            isInformationMatter={isInformationMatterInDocument(
              matter.decision.decisionData,
              DocumentType.board_meeting,
            )}
          />
          {onVoteForMatter && (
            <VotingButtons
              vote={vote}
              matter={matter}
              onVoteForMatter={onVoteForMatter}
              resetVote={resetVote}
            />
          )}
          <MatterMetadata matter={matter} />
          <Attachments
            attachments={matter.attachments}
            onDownloadAttachment={onDownloadAttachment}
            caption={{ key: "attachments-on-matter" }}
          />
          {commentActions && (
            <CommentsView
              decisionId={matter.decision.decisionId}
              comments={matter.comments}
              editState={editComment}
              actions={commentActions}
            />
          )}
        </VBox>
      </BodyContainer>
    );
    if (matter) {
      return fullscreen !== false ? (
        <FullScreenLayout alertStore={messageStore} centerPanel={body} />
      ) : (
        body
      );
    } else {
      return null;
    }
  },
);

export const Title = observer(({ matter }: { matter: MatterForReview }) => {
  const { t } = useTranslation();
  return (
    <h4>
      {t(getDecisionActionLabel(matter.decision.decisionData.type, matter.decision.decisionData))}
    </h4>
  );
});

const VotingButtons = observer(
  ({
    matter,
    vote,
    onVoteForMatter,
    resetVote,
  }: {
    matter: MatterForReview;
    vote: Vote | undefined;
    onVoteForMatter?: (matter: MatterForReview, vote: Vote) => void;
    resetVote?: () => void;
  }) => {
    const { t } = useTranslation();
    return (
      <Box gap={"md"} paddingY>
        <ToggleButton
          icon={"thumbs-up"}
          label={t("meetings.support-matter")}
          onClick={() => onVoteForMatter?.(matter, "for")}
          selected={vote === "for"}
        />
        <ToggleButton
          icon={"thumbs-down"}
          label={t("meetings.donot-support-matter")}
          onClick={() => onVoteForMatter?.(matter, "against")}
          selected={vote === "against"}
        />
        {resetVote && (
          <Button
            variant={"secondary"}
            icon={"arrow-counter-clockwise"}
            tooltip={t("reset")}
            onClick={resetVote}
          />
        )}
      </Box>
    );
  },
);

export const BodyContainer = observer(({ children }: { children: ReactNode }) => {
  return (
    <VBox
      gap={isMobileView() ? "sm" : "lg"}
      padding={!isMobileView() ? "lg" : undefined}
      paddingTop={isMobileView()}
      maxWidth={"xl"}
      container
      bg
    >
      {children}
    </VBox>
  );
});
